import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import QRCode from "react-qr-code";
import {
  MessageCircle,
  Wifi,
  WifiOff,
  QrCode,
  Settings,
  Bell,
  Send,
  Plus,
  Edit,
  Trash2,
  Loader2,
  ArrowLeft,
  Clock,
  Users,
  Filter,
  Calendar,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Info,
  RefreshCw,
  Copy,
  Zap,
  CalendarClock,
  Repeat,
  Webhook,
  UserCheck,
  Gift,
  Heart,
  UserPlus,
  Briefcase,
  Save,
  LogOut,
  Upload,
  Phone,
  FileSpreadsheet,
  Star,
  Eye,
  Link2,
  List,
  Download,
  PlusCircle,
  X,
  AlertTriangle,
  Check
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
import { WhatsAppAntiBlockCard } from "../components/WhatsAppAntiBlockCard";

// Types of auto-messages
const AUTO_MESSAGE_TYPES = [
  { value: "escalado", label: "Quando Escalado", icon: Calendar, description: "Enviado quando a pessoa é escalada para um evento" },
  { value: "lembrete_evento", label: "Lembrete de Evento", icon: Bell, description: "Enviado X horas antes do evento" },
  { value: "aprovado_voluntario", label: "Voluntário Aprovado", icon: UserCheck, description: "Enviado quando aprovado como voluntário" },
  { value: "aniversario", label: "Aniversário", icon: Gift, description: "Enviado no dia do aniversário" },
  { value: "cadastro_sistema", label: "Novo Cadastro", icon: UserPlus, description: "Enviado quando pessoa é cadastrada" },
  { value: "alteracao_escala", label: "Alteração na Escala", icon: AlertCircle, description: "Enviado quando a escala é alterada" },
  { value: "situacao", label: "Por Situação", icon: Heart, description: "Enviado quando pessoa marca uma situação específica" },
  { value: "projeto_social", label: "Projeto Social", icon: Briefcase, description: "Enviado quando se inscreve em projeto social" },
  { value: "lideranca_notificacao", label: "Notificação Liderança", icon: Users, description: "Notifica líderes sobre novos cadastros" },
];

const SEND_TYPES = [
  { value: "imediato", label: "Envio Imediato", icon: Zap },
  { value: "agendado", label: "Agendado", icon: CalendarClock },
  { value: "recorrente", label: "Recorrente", icon: Repeat },
];

const RECURRENCE_OPTIONS = [
  { value: "diario", label: "Diário" },
  { value: "semanal", label: "Semanal" },
  { value: "mensal", label: "Mensal" },
];

const DAYS_OF_WEEK = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
];

export default function WhatsAppConfigPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("chat");
  
  // Connection state
  const [whatsappStatus, setWhatsappStatus] = useState({
    connected: false,
    qr: null,
    status: "checking",
    user: null
  });
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  
  // Global WhatsApp Settings (migrated from SettingsPage)
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: false,
    tempo_antes_evento: "2",
    unidade_tempo: "horas",
    mensagem_escala: `Olá *{nome}*! 👋

Você foi escalado(a) para servir:

📅 *{evento}*
🗓️ Data: {data}
⏰ Horário: {horario}
📍 Local: {local}

🎯 Sua função: *{funcao}*
🕐 Chegada: {hora_voluntario}

🎵 Músicas:
{musicas}

✅ CONFIRMAR PRESENÇA
{link_confirmacao}

📱 VER MINHA ESCALA
{link}

Deus abençoe! 🙏`,
    mensagem_lembrete: `⏰ *LEMBRETE DE ESCALA* ⏰

Olá *{nome}*!

Não esqueça, você está escalado(a) para *HOJE*!

📅 *{evento}*
⏰ Horário: {horario}
📍 Local: {local}
🎯 Função: *{funcao}*

📱 VER MINHA ESCALA
{link}

Contamos com você! 🙏`,
    mensagem_aprovacao: "",
    mensagem_cadastro: "",
    notificar_alteracao_escala: false,
    notificar_alteracao_evento: true,
    notificar_alteracao_horario: true,
    notificar_alteracao_local: true,
    notificar_alteracao_musicas: true,
    notificar_alteracao_observacoes: false,
    mensagem_alteracao: `⚠️ *ALTERAÇÃO NA ESCALA* ⚠️

Olá *{nome}*!

Houve uma alteração na escala em que você está escalado(a):

📅 *{evento}*
🗓️ Data: {data}
⏰ Horário: {horario}
📍 Local: {local}

📝 *Alterações:*
{alteracoes}

📱 VER ESCALA ATUALIZADA
{link}

Qualquer dúvida, entre em contato! 🙏`,
    // Mensagem de confirmação de inscrição
    mensagem_inscricao: `✅ *INSCRIÇÃO CONFIRMADA* ✅

Olá *{nome}*! 

Sua inscrição foi realizada com sucesso:

📌 *{projeto}*
📅 Data: {data_evento}
⏰ Horário: {horario_evento}
📍 Local: {local_evento}

{info_pagamento}

Obrigado por se inscrever! 🙏`,
    // Configurações de proteção contra bloqueio
    intervalo_minimo_segundos: 10,
    intervalo_maximo_segundos: 20,
    pausa_a_cada_mensagens: 50,
    duracao_pausa_segundos: 120,
  });

  // Birthday settings
  const [birthdaySettings, setBirthdaySettings] = useState({
    enabled: false,
    notificar_pessoa: true,
    notificar_lideres: true,
    mensagem: "🎂 Feliz Aniversário, {nome}! 🎉\n\nQue Deus abençoe grandemente a sua vida neste novo ano!\n\nCom carinho,\nEquipe da Igreja"
  });
  
  // Auto-messages state (Lembretes Fixos)
  const [autoMessages, setAutoMessages] = useState([]);
  const [autoMessageDialog, setAutoMessageDialog] = useState(false);
  const [editingAutoMessage, setEditingAutoMessage] = useState(null);
  const [autoMessageForm, setAutoMessageForm] = useState({
    nome: "",
    tipo: "escalado",
    ativo: true,
    mensagem: "",
    situacao_filtro: [],
    projeto_filtro: [],
    ministerio_filtro: [],
    antecedencia_horas: 24,
  });
  
  // Scheduled messages state
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [scheduledDialog, setScheduledDialog] = useState(false);
  const [editingScheduled, setEditingScheduled] = useState(null);
  const [scheduledForm, setScheduledForm] = useState({
    nome: "",
    mensagem: "",
    mensagens_sequenciais: ["", "", "", "", ""],
    tipo_envio: "imediato",
    data_envio: "",
    recorrencia: "semanal",
    dias_semana: [],
    horario: "09:00",
    conexao_id: null,
    fonte_contatos: "filtros",
    lista_contatos_id: null,
    filtro_ministerio: [],
    filtro_funcao: [],
    filtro_evento: [],
    filtro_situacao: [],
    filtro_genero: [],
    filtro_idade_min: null,
    filtro_idade_max: null,
    filtro_projeto_inscrito: [],
    filtro_projeto_nao_inscrito: [],
    abrir_ticket: false,
    arquivo_url: null,
  });
  
  // Options for filters
  const [ministries, setMinistries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [events, setEvents] = useState([]);
  const [situacoes, setSituacoes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatContacts, setChatContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchContact, setSearchContact] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Multiple connections state
  const [connections, setConnections] = useState([]);
  const [connectionDialog, setConnectionDialog] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [connectionForm, setConnectionForm] = useState({
    nome: "",
    service_url: "",
    is_principal: false
  });

  // Contact lists state
  const [contactLists, setContactLists] = useState([]);
  const [contactListDialog, setContactListDialog] = useState(false);
  const [editingContactList, setEditingContactList] = useState(null);
  const [contactListForm, setContactListForm] = useState({
    nome: "",
    descricao: "",
    contatos: []
  });
  const [uploadingContacts, setUploadingContacts] = useState(false);
  const [viewingListContacts, setViewingListContacts] = useState(null);
  
  // Contact editing states
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [editingContactData, setEditingContactData] = useState({ nome: "", telefone: "" });
  const [addingContactToList, setAddingContactToList] = useState(null);
  const [manualContactForm, setManualContactForm] = useState({ nome: "", telefone: "" });

  // QR code for specific connection
  const [connectionQRDialog, setConnectionQRDialog] = useState(false);
  const [connectionQRData, setConnectionQRData] = useState({ qr: null, loading: false, error: null, connection: null });

  // Active message tab for sequential messages
  const [activeMsgTab, setActiveMsgTab] = useState(0);

  // Load chat contacts
  const loadChatContacts = async (search = "") => {
    setLoadingContacts(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await axios.get(`${API}/whatsapp/chat/contacts${params}`);
      setChatContacts(res.data);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Load messages for selected contact
  const loadChatMessages = async (pessoaId) => {
    try {
      const res = await axios.get(`${API}/whatsapp/chat/messages/${pessoaId}`);
      setChatMessages(res.data);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    }
  };

  // Send direct message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;
    
    setSendingMessage(true);
    try {
      const res = await axios.post(`${API}/whatsapp/chat/send`, {
        telefone: selectedContact.telefone,
        mensagem: newMessage,
        pessoa_id: selectedContact.id
      });
      
      if (res.data.success) {
        toast.success("Mensagem enviada!");
      } else {
        toast.warning(res.data.message);
      }
      
      // Reload messages
      await loadChatMessages(selectedContact.id);
      setNewMessage("");
    } catch (error) {
      toast.error("Erro ao enviar mensagem");
    } finally {
      setSendingMessage(false);
    }
  };

  // Select contact and load messages
  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    await loadChatMessages(contact.id);
  };

  // Search contacts with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "chat") {
        loadChatContacts(searchContact);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchContact, activeTab]);

  useEffect(() => {
    loadData();
  }, []);

  // Poll WhatsApp status when on conexao tab
  useEffect(() => {
    if (activeTab === "conexao" && whatsappSettings.enabled) {
      loadWhatsappStatus();
      const interval = setInterval(loadWhatsappStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, whatsappSettings.enabled]);

  // Poll for new messages when chat is open
  useEffect(() => {
    if (activeTab === "chat" && selectedContact) {
      const interval = setInterval(() => {
        loadChatMessages(selectedContact.id);
      }, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab, selectedContact]);

  // Load contacts when switching to chat tab
  useEffect(() => {
    if (activeTab === "chat") {
      loadChatContacts();
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [
        autoRes, schedRes, minRes, funcRes, evtRes, sitRes, projRes, tplRes,
        whatsappSettingsRes, birthdayRes, connectionsRes, contactListsRes
      ] = await Promise.all([
        axios.get(`${API}/whatsapp/auto-messages`).catch(() => ({ data: [] })),
        axios.get(`${API}/whatsapp/scheduled-messages`).catch(() => ({ data: [] })),
        axios.get(`${API}/ministries`).catch(() => ({ data: [] })),
        axios.get(`${API}/functions`).catch(() => ({ data: [] })),
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/whatsapp/situacoes`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/whatsapp/templates`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/whatsapp`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/birthday-notifications`).catch(() => ({ data: null })),
        axios.get(`${API}/whatsapp/connections`).catch(() => ({ data: [] })),
        axios.get(`${API}/whatsapp/contact-lists`).catch(() => ({ data: [] })),
      ]);
      
      setAutoMessages(autoRes.data || []);
      setScheduledMessages(schedRes.data || []);
      setMinistries(minRes.data || []);
      setFunctions(funcRes.data || []);
      setEvents(evtRes.data || []);
      setSituacoes(sitRes.data || []);
      setProjects(projRes.data || []);
      setTemplates(tplRes.data);
      setConnections(connectionsRes.data || []);
      setContactLists(contactListsRes.data || []);
      
      if (whatsappSettingsRes.data) {
        setWhatsappSettings(prev => ({ ...prev, ...whatsappSettingsRes.data }));
      }
      if (birthdayRes.data) {
        setBirthdaySettings(prev => ({ ...prev, ...birthdayRes.data }));
      }

      // Load initial WhatsApp connection status
      loadWhatsappStatus();
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const loadWhatsappStatus = async () => {
    try {
      const [statusRes, qrRes] = await Promise.all([
        axios.get(`${API}/whatsapp/status`),
        axios.get(`${API}/whatsapp/qr`)
      ]);
      setWhatsappStatus({
        connected: statusRes.data?.connected || false,
        qr: qrRes.data?.qr || null,
        status: statusRes.data?.status || 'disconnected',
        user: statusRes.data?.user || null
      });
    } catch (error) {
      console.log("WhatsApp service unavailable");
      setWhatsappStatus(prev => ({ ...prev, status: 'service_unavailable' }));
    }
  };

  const handleWhatsappRefresh = async () => {
    setLoadingWhatsapp(true);
    await loadWhatsappStatus();
    setLoadingWhatsapp(false);
  };

  const handleWhatsappDisconnect = async () => {
    setLoadingWhatsapp(true);
    try {
      await axios.post(`${API}/whatsapp/disconnect`);
      toast.success("WhatsApp desconectado!");
      setWhatsappStatus({
        connected: false,
        qr: null,
        status: 'disconnected',
        user: null
      });
      // Reload status after a delay
      setTimeout(loadWhatsappStatus, 2000);
    } catch (error) {
      toast.error("Erro ao desconectar WhatsApp");
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  // Save all WhatsApp settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/settings/whatsapp`, whatsappSettings),
        axios.put(`${API}/settings/birthday-notifications`, birthdaySettings),
      ]);
      toast.success("Configurações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  // ============== CONTACT LIST MANAGEMENT ==============
  const handleSaveContactEdit = async (idx) => {
    if (!viewingListContacts || !editingContactData.telefone) return;
    
    try {
      const updatedContacts = [...viewingListContacts.contatos];
      updatedContacts[idx] = { ...editingContactData };
      
      await axios.put(`${API}/whatsapp/contact-lists/${viewingListContacts.id}`, {
        nome: viewingListContacts.nome,
        descricao: viewingListContacts.descricao,
        contatos: updatedContacts
      });
      
      setViewingListContacts(prev => ({ ...prev, contatos: updatedContacts }));
      setEditingContactIndex(null);
      toast.success("Contato atualizado");
      loadData();
    } catch (error) {
      toast.error("Erro ao salvar contato");
    }
  };

  const handleDeleteContactFromList = async (idx) => {
    if (!viewingListContacts) return;
    if (!confirm(`Remover ${viewingListContacts.contatos[idx]?.nome || "este contato"}?`)) return;
    
    try {
      const updatedContacts = viewingListContacts.contatos.filter((_, i) => i !== idx);
      
      await axios.put(`${API}/whatsapp/contact-lists/${viewingListContacts.id}`, {
        nome: viewingListContacts.nome,
        descricao: viewingListContacts.descricao,
        contatos: updatedContacts
      });
      
      setViewingListContacts(prev => ({ ...prev, contatos: updatedContacts }));
      toast.success("Contato removido");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover contato");
    }
  };

  const handleAddContactToList = async () => {
    if (!viewingListContacts || !manualContactForm.telefone) return;
    
    try {
      const newContact = {
        nome: manualContactForm.nome || "Sem nome",
        telefone: manualContactForm.telefone.replace(/\D/g, "")
      };
      
      const updatedContacts = [...viewingListContacts.contatos, newContact];
      
      await axios.put(`${API}/whatsapp/contact-lists/${viewingListContacts.id}`, {
        nome: viewingListContacts.nome,
        descricao: viewingListContacts.descricao,
        contatos: updatedContacts
      });
      
      setViewingListContacts(prev => ({ ...prev, contatos: updatedContacts }));
      setManualContactForm({ nome: "", telefone: "" });
      setAddingContactToList(null);
      toast.success("Contato adicionado");
      loadData();
    } catch (error) {
      toast.error("Erro ao adicionar contato");
    }
  };

  // ============== CONNECTION QR CODE ==============
  const openConnectionQRDialog = async (connection) => {
    setConnectionQRData({ qr: null, loading: true, error: null, connection });
    setConnectionQRDialog(true);
    
    try {
      const res = await axios.get(`${API}/whatsapp/connections/${connection.id}/qr`);
      setConnectionQRData({
        qr: res.data.qr,
        loading: false,
        error: res.data.error || null,
        connection,
        session_id: res.data.session_id,
        needs_multi_session: res.data.needs_multi_session
      });
    } catch (error) {
      setConnectionQRData({
        qr: null,
        loading: false,
        error: error.response?.data?.detail || "Erro ao buscar QR Code",
        connection
      });
    }
  };

  const refreshConnectionQR = async () => {
    if (!connectionQRData.connection) return;
    setConnectionQRData(prev => ({ ...prev, loading: true }));
    
    try {
      const res = await axios.get(`${API}/whatsapp/connections/${connectionQRData.connection.id}/qr`);
      setConnectionQRData(prev => ({
        ...prev,
        qr: res.data.qr,
        loading: false,
        error: res.data.error || null,
        session_id: res.data.session_id,
        needs_multi_session: res.data.needs_multi_session
      }));
    } catch (error) {
      setConnectionQRData(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.detail || "Erro ao atualizar QR Code"
      }));
    }
  };

  // ============== AUTO-MESSAGES ==============
  const openAutoMessageDialog = (message = null) => {
    if (message) {
      setEditingAutoMessage(message);
      setAutoMessageForm({
        nome: message.nome || "",
        tipo: message.tipo || "escalado",
        ativo: message.ativo !== false,
        mensagem: message.mensagem || "",
        situacao_filtro: message.situacao_filtro || [],
        projeto_filtro: message.projeto_filtro || [],
        ministerio_filtro: message.ministerio_filtro || [],
        antecedencia_horas: message.antecedencia_horas || 24,
      });
    } else {
      setEditingAutoMessage(null);
      setAutoMessageForm({
        nome: "",
        tipo: "escalado",
        ativo: true,
        mensagem: "",
        situacao_filtro: [],
        projeto_filtro: [],
        ministerio_filtro: [],
        antecedencia_horas: 24,
      });
    }
    setAutoMessageDialog(true);
  };

  const handleSaveAutoMessage = async () => {
    if (!autoMessageForm.nome || !autoMessageForm.mensagem) {
      toast.error("Preencha nome e mensagem");
      return;
    }

    try {
      if (editingAutoMessage) {
        await axios.put(`${API}/whatsapp/auto-messages/${editingAutoMessage.id}`, autoMessageForm);
        toast.success("Mensagem atualizada!");
      } else {
        await axios.post(`${API}/whatsapp/auto-messages`, autoMessageForm);
        toast.success("Mensagem criada!");
      }
      setAutoMessageDialog(false);
      loadData();
    } catch (error) {
      toast.error("Erro ao salvar");
    }
  };

  const handleDeleteAutoMessage = async (id) => {
    if (!window.confirm("Excluir esta mensagem automática?")) return;
    try {
      await axios.delete(`${API}/whatsapp/auto-messages/${id}`);
      toast.success("Excluída!");
      loadData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleToggleAutoMessage = async (id) => {
    try {
      const res = await axios.patch(`${API}/whatsapp/auto-messages/${id}/toggle`);
      toast.success(res.data.ativo ? "Ativada" : "Desativada");
      loadData();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  // ============== SCHEDULED MESSAGES ==============
  const openScheduledDialog = (message = null) => {
    if (message) {
      setEditingScheduled(message);
      setScheduledForm({
        nome: message.nome || "",
        mensagem: message.mensagem || "",
        mensagens_sequenciais: message.mensagens_sequenciais || [message.mensagem || "", "", "", "", ""],
        tipo_envio: message.tipo_envio || "imediato",
        data_envio: message.data_envio || "",
        recorrencia: message.recorrencia || "semanal",
        dias_semana: message.dias_semana || [],
        horario: message.horario || "09:00",
        conexao_id: message.conexao_id || null,
        fonte_contatos: message.fonte_contatos || "filtros",
        lista_contatos_id: message.lista_contatos_id || null,
        filtro_ministerio: message.filtro_ministerio || [],
        filtro_funcao: message.filtro_funcao || [],
        filtro_evento: message.filtro_evento || [],
        filtro_situacao: message.filtro_situacao || [],
        filtro_genero: message.filtro_genero || [],
        filtro_idade_min: message.filtro_idade_min || null,
        filtro_idade_max: message.filtro_idade_max || null,
        filtro_projeto_inscrito: message.filtro_projeto_inscrito || [],
        filtro_projeto_nao_inscrito: message.filtro_projeto_nao_inscrito || [],
        abrir_ticket: message.abrir_ticket || false,
        arquivo_url: message.arquivo_url || null,
      });
    } else {
      setEditingScheduled(null);
      setScheduledForm({
        nome: "",
        mensagem: "",
        mensagens_sequenciais: ["", "", "", "", ""],
        tipo_envio: "imediato",
        data_envio: "",
        recorrencia: "semanal",
        dias_semana: [],
        horario: "09:00",
        conexao_id: null,
        fonte_contatos: "filtros",
        lista_contatos_id: null,
        filtro_ministerio: [],
        filtro_funcao: [],
        filtro_evento: [],
        filtro_situacao: [],
        filtro_genero: [],
        filtro_idade_min: null,
        filtro_idade_max: null,
        filtro_projeto_inscrito: [],
        filtro_projeto_nao_inscrito: [],
        abrir_ticket: false,
        arquivo_url: null,
      });
    }
    setActiveMsgTab(0);
    setPreviewCount(null);
    setScheduledDialog(true);
  };

  const handleSaveScheduled = async () => {
    // Validate name
    if (!scheduledForm.nome) {
      toast.error("Preencha o nome da campanha");
      return;
    }
    
    // Validate at least one message (either mensagem or first sequential message)
    const hasMessage = scheduledForm.mensagem || 
      (scheduledForm.mensagens_sequenciais && scheduledForm.mensagens_sequenciais.some(m => m && m.trim()));
    
    if (!hasMessage) {
      toast.error("Preencha pelo menos uma mensagem");
      return;
    }
    
    // Validate lista_contatos_id when fonte_contatos is 'lista'
    if (scheduledForm.fonte_contatos === "lista" && !scheduledForm.lista_contatos_id) {
      toast.error("Selecione uma lista de contatos");
      return;
    }

    try {
      if (editingScheduled) {
        await axios.put(`${API}/whatsapp/scheduled-messages/${editingScheduled.id}`, scheduledForm);
        toast.success("Mensagem atualizada!");
      } else {
        await axios.post(`${API}/whatsapp/scheduled-messages`, scheduledForm);
        toast.success("Mensagem criada!");
      }
      setScheduledDialog(false);
      loadData();
    } catch (error) {
      toast.error("Erro ao salvar");
    }
  };

  const handleDeleteScheduled = async (id) => {
    if (!window.confirm("Excluir esta mensagem?")) return;
    try {
      await axios.delete(`${API}/whatsapp/scheduled-messages/${id}`);
      toast.success("Excluída!");
      loadData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleSendNow = async (id) => {
    try {
      await axios.post(`${API}/whatsapp/scheduled-messages/${id}/send`);
      toast.success("Envio iniciado!");
      loadData();
    } catch (error) {
      toast.error("Erro ao enviar");
    }
  };

  const handlePauseScheduled = async (id) => {
    try {
      await axios.post(`${API}/whatsapp/scheduled-messages/${id}/pause`);
      toast.success("Envio pausado");
      loadData();
    } catch (error) {
      toast.error("Erro ao pausar");
    }
  };

  const handleResumeScheduled = async (id) => {
    try {
      await axios.post(`${API}/whatsapp/scheduled-messages/${id}/resume`);
      toast.success("Envio retomado");
      loadData();
    } catch (error) {
      toast.error("Erro ao retomar");
    }
  };

  const previewRecipients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (scheduledForm.filtro_ministerio?.length) params.append("ministerio", scheduledForm.filtro_ministerio.join(","));
      if (scheduledForm.filtro_funcao?.length) params.append("funcao", scheduledForm.filtro_funcao.join(","));
      if (scheduledForm.filtro_situacao?.length) params.append("situacao", scheduledForm.filtro_situacao.join(","));
      if (scheduledForm.filtro_genero?.length) params.append("genero", scheduledForm.filtro_genero.join(","));
      if (scheduledForm.filtro_projeto_inscrito?.length) params.append("projeto_inscrito", scheduledForm.filtro_projeto_inscrito.join(","));
      if (scheduledForm.filtro_projeto_nao_inscrito?.length) params.append("projeto_nao_inscrito", scheduledForm.filtro_projeto_nao_inscrito.join(","));
      
      const res = await axios.get(`${API}/whatsapp/preview-recipients?${params.toString()}`);
      setPreviewCount(res.data);
    } catch (error) {
      console.error("Preview error:", error);
    }
  }, [scheduledForm]);

  const insertVariable = (variable) => {
    setAutoMessageForm(prev => ({
      ...prev,
      mensagem: prev.mensagem + variable
    }));
  };

  const insertVariableScheduled = (variable) => {
    setScheduledForm(prev => ({
      ...prev,
      mensagem: prev.mensagem + variable
    }));
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pendente: { label: "Pendente", variant: "secondary" },
      enviando: { label: "Enviando...", variant: "default" },
      enviado: { label: "Enviado", variant: "success" },
      erro: { label: "Erro", variant: "destructive" },
      pausado: { label: "Pausado", variant: "outline" },
    };
    const s = statusMap[status] || statusMap.pendente;
    return <Badge variant={s.variant}>{s.label}</Badge>;
  };

  // ============== CONNECTIONS ==============
  const loadConnections = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/connections`);
      setConnections(res.data || []);
    } catch (error) {
      console.error("Erro ao carregar conexões:", error);
    }
  };

  const openConnectionDialog = (conn = null) => {
    if (conn) {
      setEditingConnection(conn);
      setConnectionForm({
        nome: conn.nome || "",
        service_url: conn.service_url || "",
        is_principal: conn.is_principal || false
      });
    } else {
      setEditingConnection(null);
      setConnectionForm({
        nome: "",
        service_url: "",
        is_principal: connections.length === 0
      });
    }
    setConnectionDialog(true);
  };

  const handleSaveConnection = async () => {
    if (!connectionForm.nome) {
      toast.error("Nome da conexão é obrigatório");
      return;
    }
    setSaving(true);
    try {
      if (editingConnection) {
        await axios.put(`${API}/whatsapp/connections/${editingConnection.id}`, connectionForm);
        toast.success("Conexão atualizada!");
      } else {
        await axios.post(`${API}/whatsapp/connections`, connectionForm);
        toast.success("Conexão criada!");
      }
      setConnectionDialog(false);
      loadConnections();
    } catch (error) {
      toast.error("Erro ao salvar conexão");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConnection = async (connId) => {
    if (!window.confirm("Excluir esta conexão?")) return;
    try {
      await axios.delete(`${API}/whatsapp/connections/${connId}`);
      toast.success("Conexão excluída!");
      loadConnections();
    } catch (error) {
      toast.error("Erro ao excluir conexão");
    }
  };

  const handleSetPrincipal = async (connId) => {
    try {
      await axios.patch(`${API}/whatsapp/connections/${connId}/set-principal`);
      toast.success("Conexão definida como principal!");
      loadConnections();
    } catch (error) {
      toast.error("Erro ao definir conexão principal");
    }
  };

  // ============== CONTACT LISTS ==============
  const loadContactLists = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/contact-lists`);
      setContactLists(res.data || []);
    } catch (error) {
      console.error("Erro ao carregar listas:", error);
    }
  };

  const openContactListDialog = (list = null) => {
    if (list) {
      setEditingContactList(list);
      setContactListForm({
        nome: list.nome || "",
        descricao: list.descricao || "",
        contatos: []
      });
    } else {
      setEditingContactList(null);
      setContactListForm({
        nome: "",
        descricao: "",
        contatos: []
      });
    }
    setContactListDialog(true);
  };

  const handleSaveContactList = async () => {
    if (!contactListForm.nome) {
      toast.error("Nome da lista é obrigatório");
      return;
    }
    setSaving(true);
    try {
      if (editingContactList) {
        await axios.put(`${API}/whatsapp/contact-lists/${editingContactList.id}`, contactListForm);
        toast.success("Lista atualizada!");
      } else {
        await axios.post(`${API}/whatsapp/contact-lists`, contactListForm);
        toast.success("Lista criada!");
      }
      setContactListDialog(false);
      loadContactLists();
    } catch (error) {
      toast.error("Erro ao salvar lista");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContactList = async (listId) => {
    if (!window.confirm("Excluir esta lista de contatos?")) return;
    try {
      await axios.delete(`${API}/whatsapp/contact-lists/${listId}`);
      toast.success("Lista excluída!");
      loadContactLists();
    } catch (error) {
      toast.error("Erro ao excluir lista");
    }
  };

  const handleFileUpload = async (e, listId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingContacts(true);
    try {
      // Use FormData to upload file directly to backend
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/whatsapp/contact-lists/${listId}/upload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${res.data.novos} contatos adicionados!`);
      if (res.data.duplicados > 0) {
        toast.info(`${res.data.duplicados} duplicados ignorados`);
      }
      if (res.data.erros && res.data.erros.length > 0) {
        toast.warning(`${res.data.erros.length} erros encontrados`);
      }
      loadContactLists();
      // Reload list details if viewing
      if (viewingListContacts?.id === listId) {
        const detail = await axios.get(`${API}/whatsapp/contact-lists/${listId}`);
        setViewingListContacts(detail.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar arquivo");
    } finally {
      setUploadingContacts(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/whatsapp/contact-lists/template`, '_blank');
  };

  // Estado para adicionar contatos manualmente
  const [manualContactDialog, setManualContactDialog] = useState(false);
  const [manualContactListId, setManualContactListId] = useState(null);
  const [manualContacts, setManualContacts] = useState([{ nome: "", telefone: "" }]);

  const openManualContactDialog = (listId) => {
    setManualContactListId(listId);
    setManualContacts([{ nome: "", telefone: "" }]);
    setManualContactDialog(true);
  };

  const addManualContactRow = () => {
    setManualContacts(prev => [...prev, { nome: "", telefone: "" }]);
  };

  const removeManualContactRow = (index) => {
    setManualContacts(prev => prev.filter((_, i) => i !== index));
  };

  const updateManualContact = (index, field, value) => {
    setManualContacts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveManualContacts = async () => {
    const validContacts = manualContacts.filter(c => c.telefone.trim());
    if (validContacts.length === 0) {
      toast.error("Adicione pelo menos um contato com telefone");
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/whatsapp/contact-lists/${manualContactListId}/add-manual`, validContacts);
      toast.success(`${res.data.novos} contatos adicionados!`);
      setManualContactDialog(false);
      loadContactLists();
      // Reload list details if viewing
      if (viewingListContacts?.id === manualContactListId) {
        const detail = await axios.get(`${API}/whatsapp/contact-lists/${manualContactListId}`);
        setViewingListContacts(detail.data);
      }
    } catch (error) {
      toast.error("Erro ao adicionar contatos");
    } finally {
      setSaving(false);
    }
  };

  const handleViewListContacts = async (listId) => {
    try {
      const res = await axios.get(`${API}/whatsapp/contact-lists/${listId}`);
      setViewingListContacts(res.data);
    } catch (error) {
      toast.error("Erro ao carregar contatos da lista");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-green-500" />
            <h1 className="text-lg font-bold">WhatsApp</h1>
          </div>
          <div className="ml-auto">
            {whatsappStatus.connected ? (
              <Badge className="bg-green-500"><Wifi className="h-3 w-3 mr-1" /> Conectado</Badge>
            ) : (
              <Badge variant="secondary"><WifiOff className="h-3 w-3 mr-1" /> Desconectado</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="chat" className="gap-1 text-xs sm:text-sm">
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Chat</span>
            </TabsTrigger>
            <TabsTrigger value="conexao" className="gap-1 text-xs sm:text-sm">
              <Link2 className="h-4 w-4" />
              <span className="hidden sm:inline">Conexões</span>
            </TabsTrigger>
            <TabsTrigger value="contatos" className="gap-1 text-xs sm:text-sm">
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Listas</span>
            </TabsTrigger>
            <TabsTrigger value="mensagens" className="gap-1 text-xs sm:text-sm">
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Campanhas</span>
            </TabsTrigger>
            <TabsTrigger value="lembretes" className="gap-1 text-xs sm:text-sm">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Auto</span>
            </TabsTrigger>
            <TabsTrigger value="config" className="gap-1 text-xs sm:text-sm">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Config</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB: CHAT - Layout Mobile-First igual WhatsApp */}
          <TabsContent value="chat" className="mt-0">
            <div className="h-[calc(100vh-180px)] md:h-[600px] flex flex-col md:flex-row overflow-hidden rounded-lg border bg-white">
              {/* Lista de contatos - escondida no mobile quando chat está aberto */}
              <div className={`${selectedContact ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r bg-white`}>
                {/* Header da lista - estilo WhatsApp */}
                <div className="bg-[#008069] p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white font-medium flex-1">Conversas</span>
                  {whatsappStatus.connected ? (
                    <Badge className="bg-green-400 text-green-900 text-xs">Online</Badge>
                  ) : (
                    <Badge className="bg-yellow-400 text-yellow-900 text-xs">Offline</Badge>
                  )}
                </div>
                
                {/* Campo de busca */}
                <div className="p-2 bg-[#F0F2F5]">
                  <div className="relative">
                    <Input
                      placeholder="Pesquisar ou começar nova conversa"
                      value={searchContact}
                      onChange={(e) => setSearchContact(e.target.value)}
                      className="h-9 pl-10 bg-white rounded-lg border-0 text-sm"
                    />
                    <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                
                {/* Lista de contatos */}
                <ScrollArea className="flex-1">
                  {loadingContacts ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <div className="animate-spin h-8 w-8 border-2 border-[#008069] border-t-transparent rounded-full mx-auto mb-3"></div>
                      <p className="text-sm">Carregando contatos...</p>
                    </div>
                  ) : chatContacts.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium">Nenhum contato</p>
                      <p className="text-xs mt-1">Digite um nome para buscar</p>
                    </div>
                  ) : (
                    chatContacts.map((contact) => (
                      <button
                        key={contact.id}
                        onClick={() => handleSelectContact(contact)}
                        className={`w-full px-3 py-3 text-left hover:bg-[#F5F6F6] flex items-center gap-3 transition-colors border-b border-gray-100 ${selectedContact?.id === contact.id ? 'bg-[#F0F2F5]' : ''}`}
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00A884] to-[#008069] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {(contact.nome_completo || contact.telefone)?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-[#111B21] truncate">{contact.nome_completo || contact.telefone}</p>
                            {contact.ultima_mensagem_data && (
                              <span className="text-xs text-[#667781] flex-shrink-0 ml-2">
                                {new Date(contact.ultima_mensagem_data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {contact.ultima_mensagem_tipo === 'enviada' && (
                              <svg className="w-4 h-4 text-[#667781] flex-shrink-0" viewBox="0 0 16 15" fill="currentColor">
                                <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.456a.32.32 0 0 0 .036.49l1.16 1.05a.365.365 0 0 0 .51-.063l6.246-7.828a.365.365 0 0 0-.063-.51l.002-.002z"/>
                                <path d="M10.672 8.864l-.478-.372a.365.365 0 0 0-.51.063L4.328 15.43a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.456a.32.32 0 0 0 .036.49l1.16 1.05a.365.365 0 0 0 .51-.063l5.896-7.382a.365.365 0 0 0-.063-.51l.002-.002z" transform="translate(-2.5 -7)"/>
                              </svg>
                            )}
                            {contact.ultima_mensagem ? (
                              <p className="text-sm text-[#667781] truncate">{contact.ultima_mensagem}</p>
                            ) : contact.nome_completo ? (
                              <p className="text-sm text-[#667781] truncate">{contact.telefone}</p>
                            ) : (
                              <p className="text-sm text-[#667781] truncate">Clique para iniciar conversa</p>
                            )}
                          </div>
                        </div>
                        {contact.nao_lida && (
                          <div className="w-5 h-5 bg-[#25D366] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            1
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </ScrollArea>
              </div>
              
              {/* Área de chat - ocupa tela toda no mobile */}
              <div className={`${selectedContact ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-[#ECE5DD]`} style={{backgroundImage: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAApgAAAKYB3X3/OAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAABNSURBVEiJ7c0xDoAgEETRP8SLeP9LWRg7E2y2oBOmm4R5k/wGxhjYAHOgD+wJ4IkkvQs4gBn4lPQO4AJa4FPSzwMwA5/0f/DvOAF4ATLuChPvPAAAAABJRU5ErkJggg==")'}}>
                {selectedContact ? (
                  <>
                    {/* Header do chat - estilo WhatsApp */}
                    <div className="bg-[#008069] px-2 py-2 flex items-center gap-2 shadow-sm">
                      {/* Botão voltar - só no mobile */}
                      <button 
                        onClick={() => setSelectedContact(null)}
                        className="md:hidden p-2 -ml-1 text-white hover:bg-white/10 rounded-full"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                        {(selectedContact.nome_completo || selectedContact.telefone)?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{selectedContact.nome_completo || selectedContact.telefone}</p>
                        {selectedContact.nome_completo && (
                          <p className="text-xs text-green-100 truncate">{selectedContact.telefone}</p>
                        )}
                      </div>
                      <button
                        onClick={() => loadChatMessages(selectedContact.id)}
                        className="p-2 text-white hover:bg-white/10 rounded-full"
                        title="Atualizar mensagens"
                      >
                        <RefreshCw className="h-5 w-5" />
                      </button>
                      {!whatsappStatus.connected && (
                        <Badge className="bg-yellow-400 text-yellow-900 text-xs flex-shrink-0">
                          <WifiOff className="h-3 w-3 mr-1" />
                          Offline
                        </Badge>
                      )}
                    </div>
                    
                    {/* Área de mensagens */}
                    <ScrollArea className="flex-1 p-3">
                      {chatMessages.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="inline-block bg-[#FCF4CB] rounded-lg px-4 py-2 shadow-sm">
                            <p className="text-xs text-[#54656F]">
                              As mensagens são criptografadas de ponta a ponta. Ninguém fora desta conversa pode lê-las.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {chatMessages.map((msg, index) => {
                            const showDate = index === 0 || 
                              new Date(msg.created_at).toDateString() !== new Date(chatMessages[index-1]?.created_at).toDateString();
                            return (
                              <div key={msg.id}>
                                {showDate && (
                                  <div className="flex justify-center my-3">
                                    <span className="bg-[#E1F2FB] text-[#54656F] text-xs px-3 py-1 rounded-lg shadow-sm">
                                      {new Date(msg.created_at).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                  </div>
                                )}
                                <div className={`flex ${msg.tipo === 'enviada' ? 'justify-end' : 'justify-start'}`}>
                                  <div 
                                    className={`relative max-w-[85%] md:max-w-[65%] px-3 py-1.5 rounded-lg shadow-sm ${
                                      msg.tipo === 'enviada' 
                                        ? 'bg-[#D9FDD3] rounded-tr-none' 
                                        : 'bg-white rounded-tl-none'
                                    }`}
                                  >
                                    {/* Tail do balão */}
                                    <div 
                                      className={`absolute top-0 w-3 h-3 ${
                                        msg.tipo === 'enviada' 
                                          ? '-right-1.5 bg-[#D9FDD3]' 
                                          : '-left-1.5 bg-white'
                                      }`}
                                      style={{
                                        clipPath: msg.tipo === 'enviada' 
                                          ? 'polygon(0 0, 100% 0, 0 100%)' 
                                          : 'polygon(100% 0, 0 0, 100% 100%)'
                                      }}
                                    />
                                    <p className="text-[15px] text-[#111B21] whitespace-pre-wrap break-words">{msg.mensagem}</p>
                                    <div className="flex items-center justify-end gap-1 -mb-0.5 mt-0.5">
                                      <span className="text-[11px] text-[#667781]">
                                        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                      </span>
                                      {msg.tipo === 'enviada' && (
                                        <span className="ml-0.5">
                                          {msg.status === 'enviada' ? (
                                            <svg className="w-4 h-4 text-[#53BDEB]" viewBox="0 0 16 15" fill="currentColor">
                                              <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.456a.32.32 0 0 0 .036.49l1.16 1.05a.365.365 0 0 0 .51-.063l6.246-7.828a.365.365 0 0 0-.063-.51l.002-.002z"/>
                                              <path d="M10.672 8.864l-.478-.372a.365.365 0 0 0-.51.063L4.328 15.43a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.456a.32.32 0 0 0 .036.49l1.16 1.05a.365.365 0 0 0 .51-.063l5.896-7.382a.365.365 0 0 0-.063-.51l.002-.002z" transform="translate(-2.5 -7)"/>
                                            </svg>
                                          ) : msg.status === 'falha' ? (
                                            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                                          ) : (
                                            <svg className="w-4 h-4 text-[#667781]" viewBox="0 0 16 15" fill="currentColor">
                                              <path d="M10.91 3.316l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.456a.32.32 0 0 0 .036.49l1.16 1.05a.365.365 0 0 0 .51-.063l6.246-7.828a.365.365 0 0 0-.063-.51l.002-.002z"/>
                                            </svg>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    {msg.status === 'falha' && msg.erro && (
                                      <p className="text-xs text-red-500 mt-1 border-t border-red-200 pt-1">{msg.erro}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                    
                    {/* Input de mensagem - estilo WhatsApp */}
                    <div className="bg-[#F0F2F5] px-2 py-2 flex items-end gap-2">
                      <div className="flex-1 bg-white rounded-3xl flex items-end">
                        <Input
                          placeholder="Mensagem"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          disabled={sendingMessage}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          className="flex-1 border-0 bg-transparent focus-visible:ring-0 text-[15px] py-2.5 px-4"
                        />
                      </div>
                      <button 
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="w-12 h-12 rounded-full bg-[#008069] hover:bg-[#017561] disabled:bg-gray-300 flex items-center justify-center transition-colors flex-shrink-0"
                      >
                        {sendingMessage ? (
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                          <Send className="h-5 w-5 text-white" />
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  /* Tela de boas-vindas quando nenhum chat selecionado - só desktop */
                  <div className="hidden md:flex flex-1 flex-col items-center justify-center text-center bg-[#F0F2F5]">
                    <div className="w-72 h-72 mb-8">
                      <svg viewBox="0 0 303 172" preserveAspectRatio="xMidYMid meet" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M229.565 160.229c32.47-25.862 53.087-65.867 53.087-110.594 0-25.14-6.605-48.71-18.186-69.165-1.093 1.292-2.226 2.563-3.398 3.814L260.06.96C261.107.538 262.18.142 263.276-.23c-8.543-15.206-19.747-28.783-33.07-40.152-7.86 5.7-17.4 9.074-27.737 9.074-26.356 0-47.723-21.367-47.723-47.723 0-3.892.466-7.68 1.346-11.31-8.35-2.082-17.088-3.207-26.09-3.207-24.673 0-47.833 6.53-67.777 17.97.492 2.52.751 5.123.751 7.786 0 22.897-18.555 41.452-41.452 41.452-1.715 0-3.406-.104-5.068-.307C5.706 1.163-.187 31.682-.187 63.635c0 44.727 20.618 84.732 53.087 110.594H0v12h303v-12h-73.435z" fill="#DAF7F3"/>
                        <path d="M202.656 53.656c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12z" fill="#00A884"/>
                      </svg>
                    </div>
                    <h2 className="text-3xl font-light text-[#41525D] mb-4">8 Life WhatsApp</h2>
                    <p className="text-sm text-[#667781] max-w-md">
                      Envie e receba mensagens diretamente das pessoas cadastradas no sistema.
                      Selecione um contato ao lado para iniciar uma conversa.
                    </p>
                    <div className="mt-8 flex items-center gap-2 text-[#667781]">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-xs">Suas mensagens são armazenadas de forma segura</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* TAB: CONEXÕES - Simplificado */}
          <TabsContent value="conexao">
            <div className="space-y-4">
              {/* Conexão Simples - QR Code */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    Conectar WhatsApp
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Escaneie o QR Code para conectar seu WhatsApp
                  </p>
                </CardHeader>
                <CardContent>
                  {whatsappStatus.connected ? (
                    <div className="text-center py-6">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Wifi className="h-10 w-10 text-green-600" />
                      </div>
                      <p className="font-semibold text-green-600 text-lg">WhatsApp Conectado!</p>
                      {whatsappStatus.user && (
                        <p className="text-muted-foreground mt-2">
                          {whatsappStatus.user.name || whatsappStatus.user.id?.split(':')[0]}
                        </p>
                      )}
                      <div className="flex justify-center gap-3 mt-6">
                        <Button variant="outline" onClick={handleWhatsappRefresh} disabled={loadingWhatsapp}>
                          <RefreshCw className={`h-4 w-4 mr-2 ${loadingWhatsapp ? 'animate-spin' : ''}`} />
                          Verificar Status
                        </Button>
                        <Button variant="destructive" onClick={handleWhatsappDisconnect} disabled={loadingWhatsapp}>
                          <LogOut className="h-4 w-4 mr-2" />
                          Desconectar
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        Status atualizado automaticamente
                      </p>
                    </div>
                  ) : whatsappStatus.qr ? (
                    <div className="text-center py-6">
                      <div className="bg-white p-4 rounded-xl inline-block border-2 shadow-sm">
                        <QRCode value={whatsappStatus.qr} size={220} />
                      </div>
                      <p className="text-sm text-muted-foreground mt-4">
                        Abra o WhatsApp → Menu → Aparelhos conectados → Conectar
                      </p>
                      <Button variant="link" className="mt-2 text-green-600" onClick={handleWhatsappRefresh}>
                        QR expirou? Clique para gerar novo
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <WifiOff className="h-10 w-10 text-gray-400" />
                      </div>
                      <p className="text-muted-foreground mb-4">Clique para gerar o QR Code</p>
                      <Button onClick={handleWhatsappRefresh} disabled={loadingWhatsapp}>
                        {loadingWhatsapp ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <QrCode className="h-4 w-4 mr-2" />
                        )}
                        Gerar QR Code
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: LISTAS DE CONTATOS */}
          <TabsContent value="contatos">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <List className="h-5 w-5 text-blue-600" />
                    Listas de Contatos
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Crie listas para disparos específicos (upload de planilha)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleDownloadTemplate} size="sm" variant="outline">
                    <Download className="h-4 w-4 mr-2" /> Baixar Modelo
                  </Button>
                  <Button onClick={() => openContactListDialog()} size="sm">
                    <Plus className="h-4 w-4 mr-2" /> Nova Lista
                  </Button>
                </div>
              </div>

              {/* Lista de listas */}
              {contactLists.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">Nenhuma lista de contatos</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Crie uma lista e faça upload de uma planilha com nome e telefone
                    </p>
                    <div className="flex gap-2 justify-center mt-4">
                      <Button onClick={handleDownloadTemplate} variant="outline">
                        <Download className="h-4 w-4 mr-2" /> Baixar Modelo Excel
                      </Button>
                      <Button onClick={() => openContactListDialog()}>
                        <Plus className="h-4 w-4 mr-2" /> Criar Lista
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {contactLists.map((list) => (
                    <Card key={list.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{list.nome}</p>
                            {list.descricao && (
                              <p className="text-sm text-muted-foreground mt-1">{list.descricao}</p>
                            )}
                            <Badge variant="secondary" className="mt-2">
                              <Users className="h-3 w-3 mr-1" />
                              {list.total_contatos || 0} contatos
                            </Badge>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleViewListContacts(list.id)} title="Ver contatos">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openContactListDialog(list)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteContactList(list.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        {/* Botões de ação */}
                        <div className="mt-4 pt-4 border-t flex gap-2">
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept=".csv,.txt,.xls,.xlsx"
                              className="hidden"
                              onChange={(e) => handleFileUpload(e, list.id)}
                              disabled={uploadingContacts}
                            />
                            <div className="flex items-center justify-center gap-2 p-2 border rounded-lg hover:border-primary hover:bg-muted/50 transition-colors">
                              {uploadingContacts ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4" />
                              )}
                              <span className="text-sm">Upload Excel/CSV</span>
                            </div>
                          </label>
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => openManualContactDialog(list.id)}
                          >
                            <PlusCircle className="h-4 w-4 mr-2" />
                            Inserir Manual
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB: CONFIGURAÇÕES (Templates de Mensagens) */}
          <TabsContent value="config" className="space-y-4">
            <WhatsAppAntiBlockCard />

            {/* Tempo antes do evento */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Lembrete Automático
                </CardTitle>
                <CardDescription>Configure quando enviar o lembrete antes do evento</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2 items-center">
                  <Label>Enviar lembrete</Label>
                  <Input
                    type="number"
                    min="1"
                    max="72"
                    value={whatsappSettings.tempo_antes_evento}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, tempo_antes_evento: e.target.value })}
                    className="w-20"
                  />
                  <Select
                    value={whatsappSettings.unidade_tempo}
                    onValueChange={(v) => setWhatsappSettings({ ...whatsappSettings, unidade_tempo: v })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutos">Minutos</SelectItem>
                      <SelectItem value="horas">Horas</SelectItem>
                      <SelectItem value="dias">Dias</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">antes do evento</span>
                </div>
              </CardContent>
            </Card>

            {/* Variáveis disponíveis */}
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <span className="font-medium text-blue-900 dark:text-blue-100">Variáveis disponíveis</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["{nome}", "{evento}", "{data}", "{horario}", "{local}", "{funcao}", "{hora_voluntario}", "{musicas}", "{link}", "{link_confirmacao}", "{ministerios}", "{email}", "{senha}", "{alteracoes}"].map((v) => (
                      <Badge key={v} variant="outline" className="font-mono text-xs bg-white dark:bg-blue-900">
                        {v}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mensagem de Escala */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mensagem de Notificação de Escala</CardTitle>
                <CardDescription>Enviada quando alguém é escalado para um evento</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={whatsappSettings.mensagem_escala}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, mensagem_escala: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                />
              </CardContent>
            </Card>

            {/* Mensagem de Lembrete */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mensagem de Lembrete</CardTitle>
                <CardDescription>Enviada X horas/minutos antes do evento</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={whatsappSettings.mensagem_lembrete}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, mensagem_lembrete: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                />
              </CardContent>
            </Card>

            {/* Mensagem de Aprovação */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mensagem de Aprovação de Voluntário</CardTitle>
                <CardDescription>Variáveis: {"{nome}"}, {"{ministerios}"}, {"{funcao}"}</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={whatsappSettings.mensagem_aprovacao || ""}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, mensagem_aprovacao: e.target.value })}
                  rows={6}
                  className="font-mono text-sm"
                  placeholder="Olá {nome}! Parabéns! Você foi aprovado(a) para servir..."
                />
              </CardContent>
            </Card>

            {/* Mensagem de Cadastro */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mensagem de Cadastro Público</CardTitle>
                <CardDescription>Variáveis: {"{nome}"}, {"{email}"}, {"{senha}"}</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={whatsappSettings.mensagem_cadastro || ""}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, mensagem_cadastro: e.target.value })}
                  rows={8}
                  className="font-mono text-sm"
                  placeholder="Olá {nome}! Seu cadastro foi realizado com sucesso..."
                />
              </CardContent>
            </Card>

            {/* Notificação de Alteração */}
            <Card className="border-orange-200 dark:border-orange-800">
              <CardHeader className="bg-orange-50 dark:bg-orange-950/20">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      Notificar Alterações na Escala
                    </CardTitle>
                    <CardDescription>Enviar WhatsApp quando houver alterações (exceto pessoas)</CardDescription>
                  </div>
                  <Switch
                    checked={whatsappSettings.notificar_alteracao_escala || false}
                    onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, notificar_alteracao_escala: checked })}
                  />
                </div>
              </CardHeader>
              {whatsappSettings.notificar_alteracao_escala && (
                <CardContent className="space-y-4 pt-4">
                  <p className="text-sm font-medium">Notificar quando alterar:</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <span className="text-sm">Evento</span>
                      <Switch
                        checked={whatsappSettings.notificar_alteracao_evento !== false}
                        onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, notificar_alteracao_evento: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <span className="text-sm">Horário</span>
                      <Switch
                        checked={whatsappSettings.notificar_alteracao_horario !== false}
                        onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, notificar_alteracao_horario: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <span className="text-sm">Local</span>
                      <Switch
                        checked={whatsappSettings.notificar_alteracao_local !== false}
                        onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, notificar_alteracao_local: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <span className="text-sm">Músicas</span>
                      <Switch
                        checked={whatsappSettings.notificar_alteracao_musicas !== false}
                        onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, notificar_alteracao_musicas: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-muted/50 col-span-2">
                      <span className="text-sm">Observações</span>
                      <Switch
                        checked={whatsappSettings.notificar_alteracao_observacoes || false}
                        onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, notificar_alteracao_observacoes: checked })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Mensagem de Alteração</Label>
                    <Textarea
                      value={whatsappSettings.mensagem_alteracao || ""}
                      onChange={(e) => setWhatsappSettings({ ...whatsappSettings, mensagem_alteracao: e.target.value })}
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Aniversário */}
            <Card className="border-pink-200 dark:border-pink-800">
              <CardHeader className="bg-pink-50 dark:bg-pink-950/20">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gift className="h-4 w-4 text-pink-500" />
                      Notificação de Aniversário
                    </CardTitle>
                    <CardDescription>Enviada automaticamente no dia do aniversário</CardDescription>
                  </div>
                  <Switch
                    checked={birthdaySettings.enabled}
                    onCheckedChange={(checked) => setBirthdaySettings(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>
              </CardHeader>
              {birthdaySettings.enabled && (
                <CardContent className="space-y-4 pt-4">
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={birthdaySettings.notificar_pessoa}
                        onCheckedChange={(checked) => setBirthdaySettings(prev => ({ ...prev, notificar_pessoa: checked }))}
                      />
                      <span className="text-sm">Parabenizar aniversariante</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={birthdaySettings.notificar_lideres}
                        onCheckedChange={(checked) => setBirthdaySettings(prev => ({ ...prev, notificar_lideres: checked }))}
                      />
                      <span className="text-sm">Avisar líderes</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Mensagem de Aniversário</Label>
                    <p className="text-xs text-muted-foreground">Variável: {"{nome}"}</p>
                    <Textarea
                      value={birthdaySettings.mensagem}
                      onChange={(e) => setBirthdaySettings(prev => ({ ...prev, mensagem: e.target.value }))}
                      rows={6}
                      className="font-mono text-sm"
                    />
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Confirmação de Inscrição */}
            <Card className="border-green-200 dark:border-green-800" id="inscricao-config">
              <CardHeader className="bg-green-50 dark:bg-green-950/20">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-green-500" />
                  Confirmação de Inscrição
                </CardTitle>
                <CardDescription>
                  Enviada quando alguém se inscreve em um projeto/evento (se habilitado no projeto)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <p className="text-xs text-green-700 dark:text-green-300">
                    <strong>Variáveis:</strong> {"{nome}"}, {"{projeto}"}, {"{data_evento}"}, {"{horario_evento}"}, {"{local_evento}"}, {"{info_pagamento}"}
                  </p>
                </div>
                <Textarea
                  value={whatsappSettings.mensagem_inscricao || ""}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, mensagem_inscricao: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                  placeholder="Mensagem enviada quando alguém se inscreve..."
                />
                <p className="text-xs text-muted-foreground">
                  Ative o envio de confirmação dentro de cada projeto/evento em Inscrição → Editar projeto
                </p>
              </CardContent>
            </Card>

            {/* Proteção contra bloqueio */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  Proteção contra Bloqueio
                </CardTitle>
                <CardDescription>
                  Configure intervalos entre mensagens para evitar bloqueio do número pelo WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Intervalo mínimo entre mensagens</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="5"
                        max="120"
                        value={whatsappSettings.intervalo_minimo_segundos || 10}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, intervalo_minimo_segundos: parseInt(e.target.value) || 10 })}
                        className="w-24"
                        data-testid="intervalo-minimo"
                      />
                      <span className="text-sm text-muted-foreground">segundos</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Mínimo recomendado: 10 segundos</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Intervalo máximo entre mensagens</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="10"
                        max="300"
                        value={whatsappSettings.intervalo_maximo_segundos || 20}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, intervalo_maximo_segundos: parseInt(e.target.value) || 20 })}
                        className="w-24"
                        data-testid="intervalo-maximo"
                      />
                      <span className="text-sm text-muted-foreground">segundos</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Adiciona variação para parecer mais natural</p>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pausar a cada</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="10"
                        max="200"
                        value={whatsappSettings.pausa_a_cada_mensagens || 50}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, pausa_a_cada_mensagens: parseInt(e.target.value) || 50 })}
                        className="w-24"
                        data-testid="pausa-a-cada"
                      />
                      <span className="text-sm text-muted-foreground">mensagens</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Faz uma pausa maior para descanso</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Duração da pausa</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="30"
                        max="600"
                        value={whatsappSettings.duracao_pausa_segundos || 120}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, duracao_pausa_segundos: parseInt(e.target.value) || 120 })}
                        className="w-24"
                        data-testid="duracao-pausa"
                      />
                      <span className="text-sm text-muted-foreground">segundos</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Pausa de {Math.floor((whatsappSettings.duracao_pausa_segundos || 120) / 60)} min {(whatsappSettings.duracao_pausa_segundos || 120) % 60}s a cada {whatsappSettings.pausa_a_cada_mensagens || 50} mensagens</p>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                    <div className="text-sm text-orange-800 dark:text-orange-200">
                      <strong>Dicas para evitar bloqueio:</strong>
                      <ul className="mt-1 space-y-1 list-disc list-inside">
                        <li>Use intervalos maiores para envios em massa (&gt;100 mensagens)</li>
                        <li>Evite enviar mensagens idênticas para muitos contatos</li>
                        <li>Números novos no WhatsApp têm limites menores</li>
                        <li>Pausas mais longas a cada lote de mensagens ajudam</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveSettings} className="w-full" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salvar Todas as Configurações
            </Button>
          </TabsContent>

          {/* TAB: LEMBRETES FIXOS */}
          <TabsContent value="lembretes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Lembretes Automáticos
                    </CardTitle>
                    <CardDescription>
                      Mensagens enviadas automaticamente por ações do sistema
                    </CardDescription>
                  </div>
                  <Button onClick={() => openAutoMessageDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Novo
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {autoMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bell className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Nenhum lembrete configurado</p>
                    <p className="text-sm mt-1">Use os templates de mensagem na aba Configurações ou crie novos aqui</p>
                  </div>
                ) : (
                  autoMessages.map((msg) => {
                    const typeInfo = AUTO_MESSAGE_TYPES.find(t => t.value === msg.tipo);
                    const TypeIcon = typeInfo?.icon || Bell;
                    return (
                      <div 
                        key={msg.id}
                        className={`p-4 border rounded-lg ${!msg.ativo ? 'opacity-50 bg-gray-50' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${msg.ativo ? 'bg-green-100' : 'bg-gray-100'}`}>
                            <TypeIcon className={`h-5 w-5 ${msg.ativo ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{msg.nome}</h4>
                              <Badge variant="outline" className="text-xs">{typeInfo?.label}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{msg.mensagem}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Switch
                              checked={msg.ativo}
                              onCheckedChange={() => handleToggleAutoMessage(msg.id)}
                            />
                            <Button variant="ghost" size="icon" onClick={() => openAutoMessageDialog(msg)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteAutoMessage(msg.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: GESTÃO DE MENSAGENS */}
          <TabsContent value="mensagens">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="h-5 w-5" />
                      Gestão de Mensagens
                    </CardTitle>
                    <CardDescription>
                      Agende ou envie mensagens com filtros
                    </CardDescription>
                  </div>
                  <Button onClick={() => openScheduledDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Mensagem
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {scheduledMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Send className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Nenhuma mensagem agendada</p>
                  </div>
                ) : (
                  scheduledMessages.map((msg) => {
                    const typeInfo = SEND_TYPES.find(t => t.value === msg.tipo_envio);
                    const TypeIcon = typeInfo?.icon || Send;
                    return (
                      <div key={msg.id} className="p-4 border rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <TypeIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="font-medium">{msg.nome}</h4>
                              {getStatusBadge(msg.status)}
                              <Badge variant="outline" className="text-xs">{typeInfo?.label}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{msg.mensagem}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {msg.total_destinatarios} destinatários
                              </span>
                              {msg.enviados > 0 && (
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                  {msg.enviados} enviados
                                </span>
                              )}
                              {msg.proximo_envio && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {new Date(msg.proximo_envio).toLocaleString('pt-BR')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {msg.status === "pendente" && (
                              <Button variant="ghost" size="icon" onClick={() => handleSendNow(msg.id)} title="Enviar agora">
                                <Play className="h-4 w-4 text-green-500" />
                              </Button>
                            )}
                            {(msg.status === "enviando" || (msg.tipo_envio === "recorrente" && msg.status !== "pausado")) && (
                              <Button variant="ghost" size="icon" onClick={() => handlePauseScheduled(msg.id)} title="Pausar envio">
                                <Pause className="h-4 w-4 text-amber-500" />
                              </Button>
                            )}
                            {msg.status === "pausado" && (
                              <Button variant="ghost" size="icon" onClick={() => handleResumeScheduled(msg.id)} title="Retomar envio">
                                <Play className="h-4 w-4 text-green-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openScheduledDialog(msg)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteScheduled(msg.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog: Auto Message */}
      <Dialog open={autoMessageDialog} onOpenChange={setAutoMessageDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAutoMessage ? "Editar" : "Novo"} Lembrete Automático</DialogTitle>
            <DialogDescription>
              Configure uma mensagem que será enviada automaticamente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={autoMessageForm.nome}
                onChange={(e) => setAutoMessageForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Aviso de Escala"
              />
            </div>

            <div>
              <Label>Tipo de Disparo *</Label>
              <Select 
                value={autoMessageForm.tipo} 
                onValueChange={(v) => setAutoMessageForm(prev => ({ ...prev, tipo: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTO_MESSAGE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {AUTO_MESSAGE_TYPES.find(t => t.value === autoMessageForm.tipo)?.description}
              </p>
            </div>

            {autoMessageForm.tipo === "situacao" && (
              <div>
                <Label>Situações que disparam esta mensagem</Label>
                <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                  {situacoes.map((sit) => (
                    <div key={sit} className="flex items-center gap-2">
                      <Checkbox
                        checked={autoMessageForm.situacao_filtro?.includes(sit)}
                        onCheckedChange={(checked) => {
                          setAutoMessageForm(prev => ({
                            ...prev,
                            situacao_filtro: checked
                              ? [...(prev.situacao_filtro || []), sit]
                              : (prev.situacao_filtro || []).filter(s => s !== sit)
                          }));
                        }}
                      />
                      <span className="text-sm">{sit}</span>
                    </div>
                  ))}
                  {situacoes.length === 0 && (
                    <p className="text-sm text-muted-foreground">Nenhuma situação cadastrada</p>
                  )}
                </div>
              </div>
            )}

            {autoMessageForm.tipo === "lembrete_evento" && (
              <div>
                <Label>Antecedência (horas antes)</Label>
                <Input
                  type="number"
                  min={1}
                  max={168}
                  value={autoMessageForm.antecedencia_horas}
                  onChange={(e) => setAutoMessageForm(prev => ({ ...prev, antecedencia_horas: parseInt(e.target.value) || 24 }))}
                />
              </div>
            )}

            {autoMessageForm.tipo === "lideranca_notificacao" && (
              <div>
                <Label>Ministérios (notificar líderes destes ministérios)</Label>
                <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                  {ministries.map((min) => (
                    <div key={min.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={autoMessageForm.ministerio_filtro?.includes(min.id)}
                        onCheckedChange={(checked) => {
                          setAutoMessageForm(prev => ({
                            ...prev,
                            ministerio_filtro: checked
                              ? [...(prev.ministerio_filtro || []), min.id]
                              : (prev.ministerio_filtro || []).filter(m => m !== min.id)
                          }));
                        }}
                      />
                      <span className="text-sm">{min.nome}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Mensagem *</Label>
                {templates && (
                  <div className="flex flex-wrap gap-1">
                    {templates.variaveis.slice(0, 4).map((v) => (
                      <Button
                        key={v.codigo}
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => insertVariable(v.codigo)}
                        title={v.descricao}
                      >
                        {v.codigo}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <Textarea
                value={autoMessageForm.mensagem}
                onChange={(e) => setAutoMessageForm(prev => ({ ...prev, mensagem: e.target.value }))}
                placeholder="Digite a mensagem..."
                rows={4}
              />
              {templates?.exemplos[autoMessageForm.tipo] && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="text-xs p-0 h-auto mt-1"
                  onClick={() => setAutoMessageForm(prev => ({ ...prev, mensagem: templates.exemplos[autoMessageForm.tipo] }))}
                >
                  Usar exemplo
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm">Ativo</span>
              <Switch
                checked={autoMessageForm.ativo}
                onCheckedChange={(v) => setAutoMessageForm(prev => ({ ...prev, ativo: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoMessageDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveAutoMessage}>{editingAutoMessage ? "Salvar" : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Scheduled Message */}
      <Dialog open={scheduledDialog} onOpenChange={setScheduledDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingScheduled ? "Editar" : "Nova"} Campanha</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={scheduledForm.nome}
                  onChange={(e) => setScheduledForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Aviso Semanal"
                />
              </div>
              <div>
                <Label>Tipo de Envio</Label>
                <Select 
                  value={scheduledForm.tipo_envio} 
                  onValueChange={(v) => setScheduledForm(prev => ({ ...prev, tipo_envio: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEND_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Fonte dos contatos e Conexão */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fonte dos Contatos</Label>
                <Select 
                  value={scheduledForm.fonte_contatos || "filtros"} 
                  onValueChange={(v) => setScheduledForm(prev => ({ ...prev, fonte_contatos: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Contatos</SelectItem>
                    <SelectItem value="filtros">Por Filtros</SelectItem>
                    <SelectItem value="lista">Lista de Contatos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Conexão WhatsApp</Label>
                <Select 
                  value={scheduledForm.conexao_id || "auto"} 
                  onValueChange={(v) => setScheduledForm(prev => ({ ...prev, conexao_id: v === "auto" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auto (principal)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (primeira conectada)</SelectItem>
                    {connections.map((conn) => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.nome} {conn.is_principal ? "(Principal)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Seletor de Lista de Contatos */}
            {scheduledForm.fonte_contatos === "lista" && (
              <div>
                <Label>Selecione a Lista de Contatos *</Label>
                <Select 
                  value={scheduledForm.lista_contatos_id || ""} 
                  onValueChange={(v) => setScheduledForm(prev => ({ ...prev, lista_contatos_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma lista..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contactLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.nome} ({list.total_contatos || 0} contatos)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {contactLists.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Nenhuma lista encontrada. Crie uma na aba "Listas".
                  </p>
                )}
              </div>
            )}

            {scheduledForm.tipo_envio === "agendado" && (
              <div>
                <Label>Data e Hora do Envio</Label>
                <Input
                  type="datetime-local"
                  value={scheduledForm.data_envio}
                  onChange={(e) => setScheduledForm(prev => ({ ...prev, data_envio: e.target.value }))}
                />
              </div>
            )}

            {scheduledForm.tipo_envio === "recorrente" && (
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Recorrência</Label>
                    <Select 
                      value={scheduledForm.recorrencia} 
                      onValueChange={(v) => setScheduledForm(prev => ({ ...prev, recorrencia: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RECURRENCE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Horário</Label>
                    <Input
                      type="time"
                      value={scheduledForm.horario}
                      onChange={(e) => setScheduledForm(prev => ({ ...prev, horario: e.target.value }))}
                    />
                  </div>
                </div>
                {scheduledForm.recorrencia === "semanal" && (
                  <div>
                    <Label>Dias da Semana</Label>
                    <div className="flex gap-2 mt-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <Button
                          key={day.value}
                          type="button"
                          variant={scheduledForm.dias_semana?.includes(day.value) ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setScheduledForm(prev => ({
                              ...prev,
                              dias_semana: prev.dias_semana?.includes(day.value)
                                ? prev.dias_semana.filter(d => d !== day.value)
                                : [...(prev.dias_semana || []), day.value]
                            }));
                          }}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mensagens Sequenciais */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  Mensagens (envio sequencial)
                </Label>
                {templates && (
                  <div className="flex flex-wrap gap-1">
                    {templates.variaveis.slice(0, 4).map((v) => (
                      <Button
                        key={v.codigo}
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => {
                          const msgs = [...(scheduledForm.mensagens_sequenciais || ["", "", "", "", ""])];
                          msgs[activeMsgTab] = (msgs[activeMsgTab] || "") + v.codigo;
                          setScheduledForm(prev => ({ ...prev, mensagens_sequenciais: msgs, mensagem: msgs[0] }));
                        }}
                        title={v.descricao}
                      >
                        {v.codigo}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              
              <Tabs value={`msg-${activeMsgTab}`} onValueChange={(v) => setActiveMsgTab(parseInt(v.split('-')[1]))} className="w-full">
                <TabsList className="w-full justify-start">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <TabsTrigger key={i} value={`msg-${i}`} className="text-sm">
                      MSG. {i + 1}
                      {(scheduledForm.mensagens_sequenciais?.[i] || (i === 0 ? scheduledForm.mensagem : "")) && (
                        <span className="ml-1 w-2 h-2 rounded-full bg-green-500" />
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {[0, 1, 2, 3, 4].map((i) => (
                  <TabsContent key={i} value={`msg-${i}`} className="mt-3">
                    <Textarea
                      value={scheduledForm.mensagens_sequenciais?.[i] || (i === 0 ? scheduledForm.mensagem : "")}
                      onChange={(e) => {
                        const msgs = [...(scheduledForm.mensagens_sequenciais || [scheduledForm.mensagem || "", "", "", "", ""])];
                        msgs[i] = e.target.value;
                        setScheduledForm(prev => ({ 
                          ...prev, 
                          mensagens_sequenciais: msgs,
                          mensagem: msgs[0] // Keep mensagem in sync with first message
                        }));
                      }}
                      placeholder={`Mensagem ${i + 1}${i === 0 ? " (obrigatória)" : " (opcional)"}`}
                      rows={4}
                    />
                  </TabsContent>
                ))}
              </Tabs>
              <p className="text-xs text-muted-foreground">
                Use variáveis como {"{nome}"}, {"{numero}"}. Mensagens vazias serão ignoradas.
              </p>
            </div>

            {/* Filters - only show if fonte_contatos is "filtros" */}
            {scheduledForm.fonte_contatos === "filtros" && (
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Filter className="h-4 w-4" />
                  <Label className="font-medium">Filtros de Destinatários</Label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Ministério</Label>
                    <div className="mt-1 space-y-1 max-h-24 overflow-y-auto border rounded p-2">
                      {ministries.map((min) => (
                        <div key={min.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={scheduledForm.filtro_ministerio?.includes(min.id)}
                            onCheckedChange={(checked) => {
                              setScheduledForm(prev => ({
                                ...prev,
                                filtro_ministerio: checked
                                  ? [...(prev.filtro_ministerio || []), min.id]
                                  : (prev.filtro_ministerio || []).filter(m => m !== min.id)
                              }));
                            }}
                          />
                          <span className="text-xs">{min.nome}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Função</Label>
                    <div className="mt-1 space-y-1 max-h-24 overflow-y-auto border rounded p-2">
                      {functions.map((func) => (
                        <div key={func.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={scheduledForm.filtro_funcao?.includes(func.id)}
                            onCheckedChange={(checked) => {
                              setScheduledForm(prev => ({
                                ...prev,
                                filtro_funcao: checked
                                  ? [...(prev.filtro_funcao || []), func.id]
                                  : (prev.filtro_funcao || []).filter(f => f !== func.id)
                              }));
                            }}
                          />
                          <span className="text-xs">{func.nome}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Situação</Label>
                    <div className="mt-1 space-y-1 max-h-24 overflow-y-auto border rounded p-2">
                      {situacoes.map((sit) => (
                        <div key={sit} className="flex items-center gap-2">
                          <Checkbox
                            checked={scheduledForm.filtro_situacao?.includes(sit)}
                            onCheckedChange={(checked) => {
                              setScheduledForm(prev => ({
                                ...prev,
                                filtro_situacao: checked
                                  ? [...(prev.filtro_situacao || []), sit]
                                  : (prev.filtro_situacao || []).filter(s => s !== sit)
                              }));
                            }}
                          />
                          <span className="text-xs">{sit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Gênero</Label>
                    <div className="mt-1 space-y-1 border rounded p-2">
                      {["masculino", "feminino"].map((gen) => (
                        <div key={gen} className="flex items-center gap-2">
                          <Checkbox
                            checked={scheduledForm.filtro_genero?.includes(gen)}
                            onCheckedChange={(checked) => {
                              setScheduledForm(prev => ({
                                ...prev,
                                filtro_genero: checked
                                  ? [...(prev.filtro_genero || []), gen]
                                  : (prev.filtro_genero || []).filter(g => g !== gen)
                              }));
                            }}
                          />
                          <span className="text-xs capitalize">{gen}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Idade Mínima</Label>
                    <Input
                      type="number"
                      min={0}
                      max={120}
                      value={scheduledForm.filtro_idade_min || ""}
                      onChange={(e) => setScheduledForm(prev => ({ ...prev, filtro_idade_min: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="Ex: 18"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Idade Máxima</Label>
                    <Input
                      type="number"
                      min={0}
                      max={120}
                      value={scheduledForm.filtro_idade_max || ""}
                      onChange={(e) => setScheduledForm(prev => ({ ...prev, filtro_idade_max: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="Ex: 35"
                    />
                  </div>
                </div>

                {/* Filtros de Inscrição em Projetos */}
                <div className="space-y-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-sm font-medium text-blue-700">Filtro por Inscrição em Projetos</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-green-600">Inscrito em</Label>
                      <ScrollArea className="h-24 border rounded p-2 bg-white">
                        {projects.map((proj) => (
                          <div key={proj.id} className="flex items-center gap-2 py-1">
                            <Checkbox
                              checked={scheduledForm.filtro_projeto_inscrito?.includes(proj.id)}
                              onCheckedChange={(checked) => {
                                setScheduledForm(prev => ({
                                  ...prev,
                                  filtro_projeto_inscrito: checked
                                    ? [...(prev.filtro_projeto_inscrito || []), proj.id]
                                    : (prev.filtro_projeto_inscrito || []).filter(p => p !== proj.id)
                                }));
                              }}
                            />
                            <span className="text-xs">{proj.nome}</span>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                    <div>
                      <Label className="text-sm text-red-600">Não inscrito em</Label>
                      <ScrollArea className="h-24 border rounded p-2 bg-white">
                        {projects.map((proj) => (
                          <div key={proj.id} className="flex items-center gap-2 py-1">
                            <Checkbox
                              checked={scheduledForm.filtro_projeto_nao_inscrito?.includes(proj.id)}
                              onCheckedChange={(checked) => {
                                setScheduledForm(prev => ({
                                  ...prev,
                                  filtro_projeto_nao_inscrito: checked
                                    ? [...(prev.filtro_projeto_nao_inscrito || []), proj.id]
                                    : (prev.filtro_projeto_nao_inscrito || []).filter(p => p !== proj.id)
                                }));
                              }}
                            />
                            <span className="text-xs">{proj.nome}</span>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Filtro combinado: ex. todos do ministério Louvor que NÃO estão inscritos no Retiro 2025
                  </p>
                </div>

                <Button type="button" variant="outline" size="sm" className="w-full" onClick={previewRecipients}>
                  <Users className="h-4 w-4 mr-2" />
                  {previewCount !== null ? `${previewCount.total} destinatário(s)` : "Prévia de Destinatários"}
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduledDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveScheduled}>{editingScheduled ? "Salvar" : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nova/Editar Conexão */}
      <Dialog open={connectionDialog} onOpenChange={setConnectionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingConnection ? "Editar Conexão" : "Nova Conexão WhatsApp"}
            </DialogTitle>
            <DialogDescription>
              Configure os detalhes da conexão WhatsApp
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Conexão *</Label>
              <Input
                value={connectionForm.nome}
                onChange={(e) => setConnectionForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: WhatsApp Louvor"
              />
            </div>
            <div>
              <Label>URL do Serviço Baileys (opcional)</Label>
              <Input
                value={connectionForm.service_url}
                onChange={(e) => setConnectionForm(prev => ({ ...prev, service_url: e.target.value }))}
                placeholder="Ex: http://localhost:3001"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL do servidor Baileys para esta conexão específica
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={connectionForm.is_principal}
                onCheckedChange={(checked) => setConnectionForm(prev => ({ ...prev, is_principal: checked }))}
              />
              <Label>Definir como conexão principal</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectionDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveConnection} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingConnection ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: QR Code da Conexão */}
      <Dialog open={connectionQRDialog} onOpenChange={setConnectionQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code: {connectionQRData.connection?.nome}
            </DialogTitle>
            <DialogDescription>
              Escaneie com o WhatsApp para conectar este número
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 flex flex-col items-center">
            {connectionQRData.loading ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-green-500" />
                <p className="text-sm text-muted-foreground mt-2">Gerando QR Code...</p>
              </div>
            ) : connectionQRData.error ? (
              <div className="text-center py-4">
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-red-500 mb-2">{connectionQRData.error}</p>
                {connectionQRData.needs_multi_session && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4 text-left">
                    <p className="text-sm text-amber-800 font-medium mb-2">
                      ⚠️ Multi-sessão não disponível
                    </p>
                    <p className="text-xs text-amber-700">
                      Para ter múltiplos números WhatsApp, seu serviço Baileys precisa suportar multi-sessão.
                      Cada conexão adicional requer uma instância separada do Baileys ou um serviço compatível.
                    </p>
                    <p className="text-xs text-amber-700 mt-2">
                      <strong>Dica:</strong> Use a "Conexão Padrão" na parte inferior da aba Conexões para conectar o número principal.
                    </p>
                  </div>
                )}
                <Button onClick={refreshConnectionQR} variant="outline" className="mt-4">
                  <RefreshCw className="h-4 w-4 mr-2" /> Tentar Novamente
                </Button>
              </div>
            ) : connectionQRData.qr ? (
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-lg shadow">
                  <QRCode value={connectionQRData.qr} size={200} />
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Abra o WhatsApp no celular e escaneie
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Session ID: {connectionQRData.session_id}
                </p>
                <Button onClick={refreshConnectionQR} variant="outline" className="mt-4">
                  <RefreshCw className="h-4 w-4 mr-2" /> Atualizar QR
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <Check className="h-12 w-12 text-green-500 mx-auto" />
                <p className="text-sm text-green-600 mt-2">Conexão já está ativa!</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectionQRDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nova/Editar Lista de Contatos */}
      <Dialog open={contactListDialog} onOpenChange={setContactListDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContactList ? "Editar Lista" : "Nova Lista de Contatos"}
            </DialogTitle>
            <DialogDescription>
              Crie uma lista para disparos específicos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Lista *</Label>
              <Input
                value={contactListForm.nome}
                onChange={(e) => setContactListForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Convidados Evento 2026"
              />
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Textarea
                value={contactListForm.descricao}
                onChange={(e) => setContactListForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva a finalidade desta lista..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactListDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveContactList} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingContactList ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Visualizar e Editar Contatos da Lista */}
      <Dialog open={!!viewingListContacts} onOpenChange={() => setViewingListContacts(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Contatos: {viewingListContacts?.nome}
            </DialogTitle>
            <DialogDescription>
              {viewingListContacts?.contatos?.length || 0} contatos na lista
            </DialogDescription>
          </DialogHeader>
          
          {/* Botões de ação em massa */}
          <div className="flex flex-wrap gap-2 pb-2 border-b">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setManualContactForm({ nome: "", telefone: "" });
                setAddingContactToList(viewingListContacts?.id);
              }}
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Adicionar Contato
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => document.getElementById(`upload-excel-view-${viewingListContacts?.id}`)?.click()}
            >
              <Upload className="h-4 w-4 mr-1" />
              Importar Excel
            </Button>
            <input
              type="file"
              id={`upload-excel-view-${viewingListContacts?.id}`}
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleFileUpload(e, viewingListContacts?.id);
                }
              }}
            />
          </div>

          <ScrollArea className="max-h-[400px] pr-2">
            {viewingListContacts?.contatos?.length > 0 ? (
              <div className="space-y-2">
                {viewingListContacts.contatos.map((contato, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                    {editingContactIndex === idx ? (
                      <div className="flex-1 flex gap-2 items-center">
                        <Input
                          value={editingContactData.nome || ""}
                          onChange={(e) => setEditingContactData(prev => ({ ...prev, nome: e.target.value }))}
                          placeholder="Nome"
                          className="flex-1"
                        />
                        <Input
                          value={editingContactData.telefone || ""}
                          onChange={(e) => setEditingContactData(prev => ({ ...prev, telefone: e.target.value }))}
                          placeholder="Telefone"
                          className="w-40"
                        />
                        <Button size="icon" variant="ghost" onClick={() => handleSaveContactEdit(idx)}>
                          <Check className="h-4 w-4 text-green-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditingContactIndex(null)}>
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{contato.nome || "Sem nome"}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contato.telefone}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => {
                              setEditingContactIndex(idx);
                              setEditingContactData({ ...contato });
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => handleDeleteContactFromList(idx)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nenhum contato na lista</p>
                <p className="text-sm">Adicione contatos manualmente ou importe uma planilha</p>
              </div>
            )}
          </ScrollArea>
          
          {/* Adicionar contato inline */}
          {addingContactToList === viewingListContacts?.id && (
            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Novo Contato:</p>
              <div className="flex gap-2">
                <Input
                  value={manualContactForm.nome}
                  onChange={(e) => setManualContactForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome completo"
                  className="flex-1"
                />
                <Input
                  value={manualContactForm.telefone}
                  onChange={(e) => setManualContactForm(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="Telefone (ex: 5562999998888)"
                  className="w-48"
                />
                <Button onClick={handleAddContactToList} disabled={!manualContactForm.telefone}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => setAddingContactToList(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingListContacts(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Inserir Contatos Manualmente */}
      <Dialog open={manualContactDialog} onOpenChange={setManualContactDialog}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="h-5 w-5" />
              Inserir Contatos Manualmente
            </DialogTitle>
            <DialogDescription>
              Adicione contatos digitando nome e telefone
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-3">
              {manualContacts.map((contact, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    placeholder="Nome"
                    value={contact.nome}
                    onChange={(e) => updateManualContact(idx, 'nome', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Telefone (com DDD)"
                    value={contact.telefone}
                    onChange={(e) => updateManualContact(idx, 'telefone', e.target.value)}
                    className="flex-1"
                  />
                  {manualContacts.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeManualContactRow(idx)}
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <Button variant="outline" onClick={addManualContactRow} className="w-full mt-2">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar mais uma linha
          </Button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualContactDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveManualContacts} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar Contatos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
